import { Alert, AlertType } from "@/atoms-react/alert/index.react.tsx";
import { Button } from "@/atoms-react/button/Button.react";
import Image from "@/atoms-react/image/Image.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react.tsx";
import InputLabel from "@/atoms-react/input/Input.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Modal, ModalContent, type ModalProps } from "@/atoms-react/modal/index.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react.tsx";
import { Base, InfoAlert } from "@/atoms-react/text/Text.react.tsx";
import config from "@/config";
import { CGU_TYPE, type CguType } from "@/features/cgu/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import type { KycoinAccount } from "@/features/kycoin/types";
import { useKypay } from "@/features/kypay/hooks/use-kypay";
import type { TransferKypayAmountProps, travelRulesWalletItems } from "@/features/kypay/types";
import { useUser } from "@/features/user/hooks/use-user";
import { formatCurrencyInput, formatNumber } from "@/features/utils/functions.ts";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { isSystemThemeDark } from "@/features/utils/theme";
import { Themes } from "@/types/theme";
import { LightBulbIcon } from "@heroicons/react/20/solid";
import { debounce } from "lodash";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import CopyButton from "../button-copy/index.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import { QRCodeSVG } from "qrcode.react";
import { identityPartners } from "@/features/user/types";
import { TravelRulesForm } from "../travel-rules";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { MonextWidget } from "@/features/payment/monext-widget";
import { MonextStatus } from "@/features/payment/types";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";

enum PaymentTools {
  CARD = "CARD",
  BANK_TRANSFER = "BANK_TRANSFER",
  KYPAY = "KYPAY",
  CRYPTO = "CRYPTO"
}

type feedBankProps = {
  currentAccount?: KycoinAccount;
  balanceKypay?: number;
  kypay?: number;
  btnText?: string;
  identityPartner?: identityPartners | null;
  pendingKypayParticipate?: boolean;
  allowedToKypay?: boolean;
  allowedToCardTransaction?: boolean;
  transferKypayAmount?: (data: TransferKypayAmountProps) => void;
  cguList: CguType[];
} & ModalProps;

export const ModalFeedBank = ({
  balanceKypay,
  kypay,
  transferKypayAmount,
  pendingKypayParticipate,
  currentAccount,
  allowedToKypay,
  allowedToCardTransaction,
  cguList,
  btnText,
  identityPartner,
  ...props
}: feedBankProps) => {
  const { t } = useTranslation(["wallet", "pass", "atoms"]);
  const [amountString, setAmountString] = useState<string>("0,00");
  const origin = useOrigin();
  const [amount, setAmount] = useState<number | null>(null);
  const [errorPaymentCard, setErrorPaymentCard] = useState<boolean>(false);
  const [acceptCGU, setAcceptCGU] = useState<boolean>(false);
  const [cgu, setCgu] = useState<CguType>();
  const [operationInProgress, setOperationInProgress] = useState<boolean>(false);
  const [isDark, setIsDark] = useState<boolean>(false);
  const [selectedPaymentTools, setSelectedPaymentTools] = useState<string>(
    PaymentTools.BANK_TRANSFER
  );
  const [addTravelRuleWallet, setAddTravelRulesWallet] = useState<boolean>(false);
  const { leUuid } = useCompanyContext();
  const { useCountries } = useUser();
  const { locale } = useLocale();
  const { data: countries } = useCountries(locale.language);
  const [walletRule, setWalletRule] = useState<string | null>(null);
  const { getLanguage } = usei18n();
  const [errorSession, setErrorSession] = useState<Error | null>(null);
  const [walletTravelIsPerson, setWalletTravelIsPerson] = useState<boolean>(true);
  const {
    feedKypayBalanceMutation,
    feedKypayBalanceCardSessionMutation,
    feedKypayCryptoAmountQuoteMutation,
    generateWalletAddressMutation,
    cryptoAssetsQuery,
    travelRuleWalletsQuery,
    createTravelRuleWalletsMutation
  } = useKypay();
  const { mutateAsync: feedKypayBalance } = feedKypayBalanceMutation();
  const {
    mutateAsync: generateAddressDeposit,
    isPending: loadingGenerateDepositAddress,
    data: walletAddressDeposit,
    error: errorWalletDeposit
  } = generateWalletAddressMutation();
  const { mutateAsync: createWalletTravelRule, isPending: loadingCreateWalletTravelRule } =
    createTravelRuleWalletsMutation();
  const { data: travelRulesWalletData, isLoading: loadingTravelRuleWallets } =
    travelRuleWalletsQuery({
      enabled: selectedPaymentTools === PaymentTools.CRYPTO && acceptCGU && amountString !== "0,00",
      queryKey: leUuid ? ["travel", "rules", leUuid] : ["travel", "rules"]
    });

  const {
    data: assetesQuerydata,
    isPending: loadingAssets,
    error: errorAssets
  } = cryptoAssetsQuery({
    enabled: selectedPaymentTools === PaymentTools.CRYPTO && acceptCGU && amountString !== "0,00",
    queryKey: ["crypto", "assets"]
  });

  const {
    mutateAsync: feedKypayBalanceCardPaymentSession,
    isPending: loadingCardSessionMutation,
    error
  } = feedKypayBalanceCardSessionMutation();
  const {
    mutateAsync: generateQuote,
    isPending: loadingQuote,
    data: quoteData,
    error: quoteError
  } = feedKypayCryptoAmountQuoteMutation();

  const { useCgusavedQuery, useCguCheckSave } = useUser();
  const { mutateAsync: saveCgu } = useCguCheckSave();
  const { data: savedCgu, refetch: refreshConsent } = useCgusavedQuery();

  const [blockchain, setBlockchain] = useState<string | null>(null);
  const [blockchainNetwork, setBlockchainNetwork] = useState<string | null>(null);
  const [blockchainName, setBlockchainName] = useState<string | null>(null);
  const [travelWalletRuleUuid, setTravelWalletRuleUuid] = useState<string | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [depositAddress, setDepositAddress] = useState<string | null>(null);
  const [depositQuote, setDepositQuote] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const handleGenerateDepositAddress = async (
    walletTravelRuleUuid: string | null,
    value?: string
  ) => {
    const networkKey = value;

    if (loadingGenerateDepositAddress || !blockchain || !networkKey || !walletTravelRuleUuid)
      return;
    return await generateAddressDeposit({
      assetKey: blockchain,
      networkKey: networkKey,
      walletTravelRuleUuid: walletTravelRuleUuid
    });
  };

  const handleGenerateQuote = async (value?: string) => {
    const assetKey = value;
    if (loadingQuote || !assetKey || !amount) return;
    const quote = await generateQuote({
      amount: amount.toString(),
      assetKey: assetKey
    });
    return quote;
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  useEffect(() => {
    return () => stopInterval();
  }, []);
  useEffect(() => {
    stopInterval();
  }, [amount, blockchain]);

  const handleGenerateQuotAddress = async (blockchain: string, blockchainNetwork: string) => {
    setLoading(true);
    try {
      const quote = await handleGenerateQuote(blockchain);
      if (quote) {
        setDepositQuote(quote);
        stopInterval();
        if (amount)
          intervalRef.current = setInterval(async () => {
            try {
              const quote = await generateQuote({
                amount: amount?.toString(),
                assetKey: blockchain
              });
              if (quote) {
                setDepositQuote(quote);
              }
            } catch (_error) {}
          }, 30000);
      }
      const depositAddress = await handleGenerateDepositAddress(
        travelWalletRuleUuid,
        blockchainNetwork
      );
      if (depositAddress) setDepositAddress(depositAddress);
      setLoading(false);
    } catch (_error) {
      setLoading(false);
    }
  };
  const resetInputs = () => {
    setAmount(0);
    setAmountString(formatNumber(0));
    stopInterval();
  };
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const prefersDarkMode = isSystemThemeDark();
    setIsDark(prefersDarkMode as boolean);
  }, []);
  useEffect(() => {
    if (error) setErrorSession(error);
    setTimeout(() => {
      setErrorSession(null);
    }, 5000);
  }, [error]);

  const [url, setUrl] = useState<string>();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [referenceId, setReferenceId] = useState<string | null>(null);

  const forceCursorPosition = () => {
    if (inputRef.current) {
      const inputLength = inputRef.current.value.length;
      inputRef.current.setSelectionRange(inputLength, inputLength);
    }
  };
  useEffect(() => {
    if (savedCgu && savedCgu?.length > 0) {
      const CguChecked = savedCgu.find((cgu) => cgu.TermType === CGU_TYPE.TRANSACTION);
      if (CguChecked) setAcceptCGU(true);
    }
  }, [savedCgu]);

  const handleNextStepEvent = async () => {
    if (
      selectedPaymentTools === PaymentTools.BANK_TRANSFER ||
      (selectedPaymentTools === PaymentTools.CRYPTO && quoteData && walletAddressDeposit)
    ) {
      resetInputs();
      props.onClose?.();
    } else if (selectedPaymentTools === "CARD") {
      if (amount) {
        setOperationInProgress(true);
        try {
          const response = await feedKypayBalance({
            amount: Number((amount * 100).toFixed(2)),
            description: "Alimenter mon solde"
          });
          setUrl(response.url);
        } catch (_) {
          toast.error(t("kypay.error.feed_kypay_balance"));
        }
        setOperationInProgress(false);
      }
    } else if (selectedPaymentTools === PaymentTools.KYPAY) {
      if (acceptCGU && cgu) {
        const CguChecked = savedCgu?.find((cgu) => cgu.TermType === CGU_TYPE.TRANSACTION);
        if (!CguChecked) {
          await saveCgu({ TermUuid: cgu.UUID });
          await refreshConsent();
        }
      }
      transferKypayAmount?.({
        amount: Number(((amount as number) * 100).toFixed(2))
      });
      resetInputs();
      props?.onClose?.();
    }
  };

  const getHolder = () => {
    switch (origin) {
      case FromURL.Comave:
        return "COMAVE";

      default:
        return "KYPAY";
    }
  };

  const onPaymentCbComplete = (status: MonextStatus) => {
    switch (status) {
      case MonextStatus.SUCCESS:
        toast.success(t("kypay.error.feed_kypay_card_success"));
        break;
      case MonextStatus.FAILED:
        setErrorPaymentCard(true);
        break;
      case MonextStatus.INTERNAL_ERROR:
        toast.error(t("kypay.error.feed_kypay_card_error"));
        break;
      case MonextStatus.PENDING:
        toast.success(t("kypay.error.feed_kypay_card_pending"));
        break;
    }

    if (status === MonextStatus.FAILED) return;

    resetInputs();
    props.onClose?.();
  };

  const debouncedSave = debounce(async (cgu: CguType) => {
    saveCgu({ TermUuid: cgu.UUID }).then(async () => {
      setCgu(undefined);
      await refreshConsent();
    });
  }, 2000);
  useEffect(() => {
    if (
      selectedPaymentTools === PaymentTools.BANK_TRANSFER &&
      acceptCGU &&
      cgu &&
      amountString !== "0,00"
    ) {
      const CguChecked = savedCgu?.find((cgu) => cgu.TermType === CGU_TYPE.TRANSACTION);
      if (!CguChecked) {
        debouncedSave(cgu);
      }
    }
  }, [selectedPaymentTools, acceptCGU, amountString]);
  const getContent = () => {
    switch (origin) {
      case FromURL.Partner:
        return (
          <RadioCard
            className="mt-2 min-h-full items-center rounded-tl-lg rounded-br-lg md:ml-2 md:w-1/4"
            active={selectedPaymentTools === PaymentTools.KYPAY}
            onClick={() => setSelectedPaymentTools(PaymentTools.KYPAY)}
            disabled={kypay === 0 || !allowedToKypay}
          >
            <Base
              className={twMerge(
                selectedPaymentTools === PaymentTools.KYPAY ? "text-black dark:text-black" : ""
              )}
            >
              {"KYPAY"}
            </Base>
            <div className={"mt-3 flex flex-col gap-2"}>
              <div>
                <Image
                  className={!isDark ? "w-28" : "w-1/2"}
                  src={
                    !isDark
                      ? "/images/olkypass/kypay-logo-color.png"
                      : isDark && selectedPaymentTools !== PaymentTools.KYPAY
                        ? "/images/olkypass/kypayLogoBlackpngDark.png"
                        : "/images/olkypass/kypayLogoBlackpng.png"
                  }
                  alt="kypay"
                />
              </div>
            </div>
          </RadioCard>
        );
      default:
        break;
    }
  };
  useEffect(() => {
    forceCursorPosition();
  }, [amountString]);

  useEffect(() => {
    if (url) {
      setOperationInProgress(false);
      resetInputs();
      props.onClose?.();
      location.href = url;
    }
  }, [url]);
  useEffect(() => {
    if (props.open) {
      if (savedCgu && savedCgu?.length > 0) {
        const CguChecked = savedCgu.find((cgu) => cgu.TermType === CGU_TYPE.TRANSACTION);
        if (CguChecked) setAcceptCGU(true);
      } else setAcceptCGU(false);
      setSelectedPaymentTools(PaymentTools.BANK_TRANSFER);
    }
  }, [props.open]);

  useEffect(() => {
    if (sessionId) {
      setSessionId(null);
      setReferenceId(null);
    }
  }, [selectedPaymentTools]);

  const handleCardPAyment = async () => {
    if (sessionId) {
      setSessionId(null);
      setReferenceId(null);
    }
    if (errorSession) setErrorSession(null);

    if (selectedPaymentTools === PaymentTools.CARD && acceptCGU) {
      if (amount && amount > 0) {
        const resp = await feedKypayBalanceCardPaymentSession({
          amount: Number((amount * 100).toFixed(2)),
          lang: getLanguage()
        });
        setSessionId(resp.session_id);
        setReferenceId(resp.reference);

        if (acceptCGU && cgu) {
          const CguChecked = savedCgu?.find((cgu) => cgu.TermType === CGU_TYPE.TRANSACTION);
          if (!CguChecked) {
            await saveCgu({ TermUuid: cgu.UUID });
            await refreshConsent();
          }
        }
      }
    }
  };

  const cryptoDropdownITem = (() =>
    assetesQuerydata?.data?.find((data) => data.AssetKey === blockchain)?.Network ?? [])();
  useEffect(() => {
    if (
      blockchain &&
      (blockchainNetwork || cryptoDropdownITem.length === 1) &&
      savedCgu &&
      amount &&
      travelWalletRuleUuid
    )
      handleGenerateQuotAddress(blockchain, blockchainNetwork ?? cryptoDropdownITem[0]);
  }, [blockchain, blockchainNetwork, savedCgu, cryptoDropdownITem, travelWalletRuleUuid]);
  const travelRulesWalletITems: travelRulesWalletItems[] = useMemo(
    () => [
      {
        id: 0,
        uuid: "ADD_WALLET",
        walletTypeName: "ADD_WALLET",
        walletName: getLanguage() === "fr" ? "Nouveau wallet" : "New Wallet"
      },
      ...(travelRulesWalletData ?? [])
    ],
    [travelRulesWalletData]
  );

  const walletTravelRule = useMemo(() => {
    if (!travelWalletRuleUuid) return null;
    return travelRulesWalletData?.find(
      (wr) => wr.uuid === travelWalletRuleUuid && !addTravelRuleWallet
    );
  }, [travelWalletRuleUuid, travelRulesWalletData]);
  return (
    <Modal
      className="w-full max-w-5xl sm:w-full"
      {...props}
      onClose={() => {
        resetInputs();
        props.onClose?.();
      }}
    >
      <ModalContent title={t("kypay.modal_deposit_funds.steps.step_1.header")}>
        <div className={"mt-4 flex w-full flex-col opacity-1"}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="col-span-1 md:col-span-3">
              <Checkbox
                value={acceptCGU}
                onChange={(value) => {
                  setAcceptCGU(value);
                  setCgu(
                    cguList?.find((cgu: CguType) => cgu.Type === CGU_TYPE.TRANSACTION) as CguType
                  );
                }}
              >
                <Base className="mb-1">
                  {t("kypay.modal_deposit_funds.steps.step_1.accept_cgu.part_1")}&nbsp;
                  <a
                    href={
                      getLanguage() === "fr"
                        ? cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlFr
                        : cguList?.find((cgu) => cgu.Type === CGU_TYPE.TRANSACTION)?.UrlEn
                    }
                    target="_blank"
                    className={
                      origin !== FromURL.None
                        ? "text-black underline dark:text-white"
                        : "text-sky-500"
                    }
                  >
                    {t("kypay.modal_deposit_funds.steps.step_1.accept_cgu.part_2")}
                  </a>
                  &nbsp;
                  {t("kypay.modal_deposit_funds.steps.step_1.accept_cgu.part_3")}
                </Base>
              </Checkbox>
            </div>
            <div className="col-span-1 md:col-span-3">
              <InputLabel
                value={amountString}
                onClick={forceCursorPosition}
                onChange={async (value: string) => {
                  if (value.length > 16) return;
                  if (selectedPaymentTools === PaymentTools.CARD) {
                    const script = document.getElementsByName("script")[0];
                    if (script) document.body.removeChild(script);
                    setSessionId(null);
                  }
                  const formattedValue = formatCurrencyInput(value);
                  const parsed = parseFloat(formattedValue);
                  if (!isNaN(parsed)) {
                    setAmount(parsed);
                    setAmountString(formatNumber(parsed));
                    if (selectedPaymentTools === PaymentTools.CRYPTO) {
                      if (
                        blockchain &&
                        (blockchainNetwork || cryptoDropdownITem.length === 1) &&
                        parsed &&
                        parsed >= 1.5 &&
                        savedCgu &&
                        depositAddress &&
                        depositQuote
                      ) {
                        const quote = await generateQuote({
                          amount: parsed.toString(),
                          assetKey: blockchain
                        });
                        if (quote) {
                          setDepositQuote(quote);
                          stopInterval();
                          intervalRef.current = setInterval(async () => {
                            try {
                              const quote = await generateQuote({
                                amount: parsed.toString(),
                                assetKey: blockchain
                              });
                              if (quote) {
                                setDepositQuote(quote);
                              }
                            } catch (_error) {}
                          }, 30000);
                        }
                      } else if (
                        blockchain &&
                        (blockchainNetwork || cryptoDropdownITem.length === 1) &&
                        parsed &&
                        parsed >= 1.5 &&
                        savedCgu &&
                        !depositAddress &&
                        !depositQuote
                      ) {
                        const network = blockchainNetwork ?? cryptoDropdownITem[0];
                        await handleGenerateQuotAddress(blockchain, network);
                      }
                    }
                  }
                }}
                suffixIcon={<span className="text-dark dark:text-white">€</span>}
                type="text"
                min="0.00"
                max="10000.00"
                step="0.01"
                label={t("kypay.modal_transfer_fund.input.amount_label")}
                placeholder={t("kypay.modal_transfer_fund.input.amount_placeholder")}
                ref={inputRef}
              />
              {selectedPaymentTools === PaymentTools.CRYPTO &&
                amount !== null &&
                amount < 1.5 &&
                !config.DISABLE_DESKOIN_CRYPTO && (
                  <Base className="mt-1 text-error">
                    {" "}
                    {t("kypay.modal_deposit_funds.steps.step_1.error_amount")}
                  </Base>
                )}
              {(selectedPaymentTools === PaymentTools.BANK_TRANSFER ||
                selectedPaymentTools === PaymentTools.CRYPTO) && (
                <div>
                  <div className="mt-4 flex flex-row justify-center">
                    <LightBulbIcon
                      color="none"
                      stroke={"#00AFEB"}
                      className="mr-2 w-8 text-transparent"
                    />
                    <div className="flex flex-col justify-center">
                      <Base
                        style={{ color: "#00AFEB" }}
                        className="flex flex-row justify-center text-bleu-400"
                      >
                        {" "}
                        {t("kypay.modal_transfer_fund.text-info")}{" "}
                      </Base>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex w-full flex-col justify-between md:flex-row">
            <RadioCard
              className="mt-2 min-h-full items-center rounded-tl-lg rounded-br-lg md:ml-2 md:w-1/4"
              active={selectedPaymentTools === PaymentTools.BANK_TRANSFER}
              onClick={() => setSelectedPaymentTools(PaymentTools.BANK_TRANSFER)}
            >
              <Base
                className={
                  selectedPaymentTools === PaymentTools.BANK_TRANSFER
                    ? "text-black dark:text-black"
                    : ""
                }
              >
                {t("kypay.modal_deposit_funds.steps.step_1.payment_methods.bank_transfer")}
              </Base>
              <div className={"mt-3 flex flex-col gap-2"}>
                <div>
                  <Image className={"w-1/2"} src="/images/olkypass/feeds/SEPA.png" alt="SEPA" />
                </div>
                <div>
                  <Image
                    className={"w-1/2"}
                    src="/images/olkypass/feeds/SEPA_INSTANT_FR.png"
                    alt="SEPA"
                  />
                </div>
              </div>
            </RadioCard>

            {!config.DISABLE_CARD_PAYMENT && !currentAccount?.is_principal_account && (
              <RadioCard
                className="mt-2 min-h-full items-center rounded-tl-lg rounded-br-lg md:ml-2 md:w-1/4"
                onClick={() => setSelectedPaymentTools(PaymentTools.CARD)}
                active={selectedPaymentTools === PaymentTools.CARD}
                disabled={!allowedToCardTransaction || config.DISABLE_CARD_PAYMENT}
                {...(!allowedToCardTransaction && {
                  "data-tooltip": t("kypay.modal_transfer_fund.text-card-tooltip")
                })}
              >
                <Base
                  className={twMerge(
                    selectedPaymentTools === PaymentTools.CARD ? "text-black dark:text-black" : ""
                  )}
                >
                  {t("kypay.modal_deposit_funds.steps.step_1.payment_methods.card")}
                </Base>
                <div className={"mt-3 flex gap-2"}>
                  <img src="/images/olkypass/feeds/VISA.png" alt="VISA" />
                  <img src="/images/olkypass/feeds/CB.png" alt="CB" />
                </div>
              </RadioCard>
            )}
            {getContent()}
            {origin === FromURL.Partner && identityPartner?.allow_crypto && (
              <RadioCard
                className="mt-2 min-h-full w-full items-center rounded-tl-lg rounded-br-lg md:ml-2 md:w-1/4"
                onClick={() => setSelectedPaymentTools(PaymentTools.CRYPTO)}
                active={selectedPaymentTools === PaymentTools.CRYPTO}
                disabled={config.DISABLE_DESKOIN_CRYPTO}
              >
                <Base
                  className={twMerge(
                    selectedPaymentTools === PaymentTools.CRYPTO ? "text-black dark:text-black" : ""
                  )}
                >
                  {t("kypay.modal_deposit_funds.steps.step_1.payment_methods.crypto")}
                </Base>
                <div className={"mt-3 flex gap-3"}>
                  <img src="/images/olkypass/Bitcoin.png" alt="Bitcoin" />
                  <img src="/images/olkypass/usdc.png" alt="Usdc" />
                </div>
              </RadioCard>
            )}
          </div>
          {selectedPaymentTools === PaymentTools.BANK_TRANSFER &&
            acceptCGU &&
            amountString !== "0,00" && (
              <div className={"mt-6 opacity-1 md:p-6"}>
                <div className={"flex flex w-full flex-row justify-center"}>
                  <Base className={"w-3/2 text-center font-bold text-black dark:text-white"}>
                    {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.message")}
                  </Base>
                </div>
                <div>
                  <div className="mt-4 mb-4 grid grid-cols-4 gap-4 text-center md:p-6">
                    <div className="text-black dark:text-white">
                      {" "}
                      {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.account")}
                    </div>
                    <div className={"col-span-2"}>
                      <Base className="font-bold">{getHolder()}</Base>
                    </div>
                    <div>
                      <CopyButton
                        text={"KYPAY"}
                        iconClassName={"text-black dark:text-white"}
                        succesMessage={
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.account") +
                          " " +
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item")
                        }
                      />
                    </div>
                    <div className="text-black dark:text-white">
                      {" "}
                      {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.address")}
                    </div>
                    <div className={"col-span-2"}>
                      <Base className="font-bold">{"1, Op de Leemen, L-5846 Fentange"}</Base>
                    </div>
                    <div>
                      <CopyButton
                        text={"1, Op de Leemen, L-5846 Fentange"}
                        iconClassName={"text-black dark:text-white"}
                        succesMessage={
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.account") +
                          " " +
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item")
                        }
                      />
                    </div>
                    <div className="text-black dark:text-white">
                      {" "}
                      {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.iban")}
                    </div>
                    <div className={"col-span-2"}>
                      <Base className="truncate font-bold">
                        {currentAccount?.virtual_iban?.IBAN}
                      </Base>
                    </div>
                    <div>
                      <CopyButton
                        text={currentAccount?.virtual_iban?.IBAN ?? ""}
                        className={"truncate"}
                        iconClassName={"text-black dark:text-white"}
                        succesMessage={
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.iban") +
                          " " +
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item")
                        }
                      />
                    </div>

                    <div className="text-black dark:text-white">
                      {" "}
                      {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.bic")}
                    </div>
                    <div className={"col-span-2"}>
                      <Base className="font-bold">{currentAccount?.virtual_iban?.BIC}</Base>
                    </div>
                    <div>
                      <CopyButton
                        text={currentAccount?.virtual_iban?.BIC ?? ""}
                        iconClassName={"text-black dark:text-white"}
                        succesMessage={
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.bic") +
                          " " +
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item")
                        }
                      />
                    </div>

                    <div className="text-black dark:text-white">
                      {" "}
                      {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.amount")}
                    </div>
                    <div className={"col-span-2"}>
                      <Base className="truncate font-bold">{amountString} €</Base>
                    </div>
                    <div>
                      <CopyButton
                        text={amountString}
                        iconClassName={"text-black dark:text-white"}
                        succesMessage={
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.labels.amount") +
                          " " +
                          t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item")
                        }
                      />
                    </div>
                  </div>
                  <div className={"flex"}>
                    <Alert
                      type={AlertType.Info}
                      title={t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.alert.title")}
                    >
                      <InfoAlert>
                        {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.alert.desc")}
                      </InfoAlert>
                    </Alert>
                  </div>
                </div>
              </div>
            )}
          {selectedPaymentTools === PaymentTools.KYPAY && acceptCGU && amountString !== "0,00" && (
            <div className={"mt-6 p-6 opacity-1"}>
              <div className="flex w-full justify-end">
                {" "}
                <Base className="font-bold">
                  {t("kypay.modal_deposit_funds.steps.step_2.bank_transfer.kypay_balance")}
                </Base>
                <Base className="ml-2">{balanceKypay} €</Base>{" "}
              </div>
            </div>
          )}
          {selectedPaymentTools === PaymentTools.CARD &&
            acceptCGU &&
            amountString !== "0,00" &&
            !config.DISABLE_CARD_PAYMENT && (
              <div className="mt-4">
                {loadingCardSessionMutation ? (
                  <div className="flex justify-center">
                    <Loader />{" "}
                  </div>
                ) : errorSession ? (
                  <div className="flex justify-center">
                    {" "}
                    <Base className="text-error">{t("Error_card_payment")}</Base>
                  </div>
                ) : (
                  sessionId &&
                  referenceId && (
                    <MonextWidget
                      sessionId={sessionId}
                      referenceId={referenceId}
                      onPaymentComplete={onPaymentCbComplete}
                    />
                  )
                )}
              </div>
            )}

          {selectedPaymentTools === PaymentTools.CRYPTO &&
            acceptCGU &&
            amount !== null &&
            amount >= 1.5 &&
            !config.DISABLE_DESKOIN_CRYPTO && (
              <div className="mt-4 px-4">
                <>
                  <Base className="mt-2 mt-8 mb-6 text-sm">
                    {t("kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule_descp")}
                  </Base>
                  <div className="mt-6 mb-6 flex flex-col md:flex-row">
                    <RadioCard
                      className="mt-2 min-h-full w-auto rounded-tl-lg rounded-br-lg pl-5"
                      active={!!walletTravelIsPerson}
                      onClick={() => {
                        setWalletTravelIsPerson(true);
                        if (depositAddress) setDepositAddress(null);
                        if (depositQuote) setDepositQuote(null);
                        if (travelWalletRuleUuid) setTravelWalletRuleUuid(null);
                        if (walletRule) setWalletRule(null);
                        if (addTravelRuleWallet) setAddTravelRulesWallet(false);
                        stopInterval();
                      }}
                      disabled={!allowedToKypay}
                    >
                      <div className="ml-4">
                        <Base className="font-semibold text-base">
                          {t("kypay.modal_deposit_funds.steps.step_2.crypto.is_person_title")}
                        </Base>
                        <div className={"mt-3 flex flex-col gap-2"}>
                          <Base className="text-sm">
                            {t("kypay.modal_deposit_funds.steps.step_2.crypto.is_person_desc")}
                          </Base>
                        </div>
                      </div>
                    </RadioCard>
                    <RadioCard
                      className="mt-2 min-h-full w-auto rounded-tl-lg rounded-br-lg pl-5 md:ml-4"
                      active={!walletTravelIsPerson}
                      onClick={() => {
                        setWalletTravelIsPerson(false);
                        if (depositAddress) setDepositAddress(null);
                        if (depositQuote) setDepositQuote(null);
                        if (travelWalletRuleUuid) setTravelWalletRuleUuid(null);
                        if (walletRule) setWalletRule(null);
                        if (addTravelRuleWallet) setAddTravelRulesWallet(false);
                        stopInterval();
                      }}
                      disabled={!allowedToKypay}
                    >
                      <div className="ml-4">
                        <Base className={twMerge("font-semibold text-base")}>
                          {t("kypay.modal_deposit_funds.steps.step_2.crypto.is_not_person_title")}
                        </Base>
                        <div className={"mt-3 flex flex-col gap-2"}>
                          <Base className="text-sm">
                            {t("kypay.modal_deposit_funds.steps.step_2.crypto.is_not_person_desc")}
                          </Base>
                        </div>
                      </div>
                    </RadioCard>
                  </div>
                  <Base className="mt-2 mt-8 mb-4 font-semibold text-xl">
                    {" "}
                    {t("kypay.modal_deposit_funds.steps.step_2.crypto.transfert_information")}
                  </Base>
                  {loadingAssets ? (
                    <div className="mt-4 flex w-full flex-row flex-row justify-center">
                      <Loader />
                    </div>
                  ) : errorAssets ? (
                    <div className="flex justify-center">
                      {" "}
                      <Base className="text-error"> {t("pass:olkypass.general.error_save")}</Base>
                    </div>
                  ) : (
                    assetesQuerydata?.data &&
                    assetesQuerydata?.data.length > 0 && (
                      <>
                        <div className="mt-4 flex flex-col md:flex-row">
                          <Dropdown
                            className={"mr-5 w-full"}
                            label={`${t(
                              "kypay.modal_deposit_funds.steps.step_2.crypto.label_crypto"
                            )}`}
                            items={assetesQuerydata.data.map((obj, index) => ({
                              key: obj.Name,
                              value: obj.AssetKey,
                              render: ({ key, value }) => (
                                <Fragment key={index}>
                                  <div
                                    className={
                                      "items-right mr-2 flex w-full flex-col justify-center"
                                    }
                                  >
                                    <div className={"flex w-full justify-end"}>
                                      <Base className="text-right text-base">{key}</Base>

                                      <div className={"items-right flex flex-col justify-end"}>
                                        <Image
                                          src={`/images/olkypass/icons/cryptos/${value.toLocaleUpperCase()}.svg`}
                                          alt="coin"
                                          className="item-right ml-3 h-6 w-6"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </Fragment>
                              )
                            }))}
                            showChevrons
                            value={
                              assetesQuerydata.data.find((asset) => asset.AssetKey === blockchain)
                                ?.Name
                            }
                            onChange={async ({ value, key }) => {
                              if (typeof value === "string") setBlockchain(value);
                              if (typeof key === "string") setBlockchainName(key);
                              if (blockchainNetwork) setBlockchainNetwork(null);
                              if (depositAddress) setDepositAddress(null);
                              if (depositQuote) setDepositQuote(null);
                              stopInterval();
                            }}
                          />

                          <Dropdown
                            className={"mt-4 w-full md:mt-0"}
                            disabled={
                              loadingQuote ||
                              loadingGenerateDepositAddress ||
                              cryptoDropdownITem.length === 0
                            }
                            label={t("kypay.modal_deposit_funds.steps.step_2.crypto.label_network")}
                            items={cryptoDropdownITem.map((obj, index) => ({
                              key: obj,
                              value: obj,
                              render: ({ key }) => (
                                <Fragment key={index}>
                                  <div
                                    className={
                                      "items-right mr-2 flex w-full flex-col justify-center"
                                    }
                                  >
                                    <div className={"flex w-full justify-end"}>
                                      <Base className="text-right text-base">{key}</Base>
                                    </div>
                                  </div>
                                </Fragment>
                              )
                            }))}
                            showChevrons
                            value={
                              cryptoDropdownITem.length === 1
                                ? cryptoDropdownITem[0]
                                : blockchainNetwork
                            }
                            onChange={async ({ value }) => {
                              if (typeof value === "string") setBlockchainNetwork(value);
                            }}
                          />
                        </div>
                      </>
                    )
                  )}
                  <Base className="mt-2 mt-6 mb-4 font-semibold text-xl">
                    {" "}
                    {t("kypay.modal_deposit_funds.steps.step_2.crypto.travel_rule_title")}
                  </Base>

                  {loadingTravelRuleWallets ? (
                    <div className="mt-4 flex w-full flex-row justify-center">
                      {" "}
                      <Loader />
                    </div>
                  ) : (
                    <Dropdown
                      search={true}
                      noSort={true}
                      label={t("kypay.modal_deposit_funds.steps.step_2.crypto.your_wallet")}
                      items={
                        (travelRulesWalletITems ?? []).map((data: travelRulesWalletItems) => ({
                          key: data.uuid,
                          value: data.walletName.toString(),

                          render: ({ key }) => (
                            <div className={"flex w-full pr-5"} key={key}>
                              <Base className={"ml-2"}>{data.walletName}</Base>
                            </div>
                          )
                        })) ?? []
                      }
                      value={walletRule ?? ""}
                      className={twMerge("mt-4 mb-4 w-62 w-full")}
                      onChange={(depositWallet) => {
                        if (depositWallet.key === "ADD_WALLET") {
                          setAddTravelRulesWallet(true);
                          if (depositAddress) setDepositAddress(null);
                          if (depositQuote) setDepositQuote(null);
                          setTravelWalletRuleUuid(null);
                          stopInterval();
                        } else {
                          if (travelWalletRuleUuid !== depositWallet.key) {
                            setAddTravelRulesWallet(false);
                            setTravelWalletRuleUuid(depositWallet.key);
                            if (depositAddress) setDepositAddress(null);
                            if (depositQuote) setDepositQuote(null);
                            stopInterval();
                          }
                        }
                        if (walletRule !== depositWallet.key) {
                          setWalletRule(depositWallet.key);
                        }
                      }}
                    />
                  )}

                  {(addTravelRuleWallet || travelWalletRuleUuid) && (
                    <TravelRulesForm
                      allowedToKypay={allowedToKypay ?? false}
                      countries={countries?.countries ?? []}
                      walletRule={walletTravelRule}
                      isPendingCreation={loadingCreateWalletTravelRule}
                      disable={!!travelWalletRuleUuid}
                      onValidate={async (data) => {
                        try {
                          const travelWallet = await createWalletTravelRule(data);
                          if (addTravelRuleWallet) setAddTravelRulesWallet(false);
                          setWalletRule(travelWallet.uuid);
                          setTravelWalletRuleUuid(travelWallet.uuid);
                        } catch (_error) {
                          toast.error(t("pass:olkypass.general.error_save"));
                        }
                      }}
                      isPerson={walletTravelIsPerson}
                    />
                  )}

                  <div className={"mt-6 mb-4 flex flex-col opacity-1"}>
                    {loading ? (
                      <div className="mt-4 mb-4 flex w-full justify-center">
                        <Loader />
                      </div>
                    ) : errorWalletDeposit || quoteError ? (
                      <Base className="mt-4 flex justify-center text-error">
                        {t("pass:olkypass.general.error_save")}
                      </Base>
                    ) : (
                      depositQuote &&
                      depositAddress && (
                        <div className="flex flex-col px-4 md:px-8">
                          <div className="flex flex-row justify-center">
                            <Base
                              className={
                                "w-[500px] text-center font-bold text-black dark:text-white"
                              }
                            >
                              {t("kypay.modal_deposit_funds.steps.step_2.crypto.label", {
                                crypto: blockchainName
                              })}
                            </Base>
                          </div>

                          <div className="mt-2 flex w-full flex-col justify-center md:flex-row">
                            <div className="text-center text-black md:w-40 md:text-right dark:text-white">
                              <Base>{`${blockchain} :`}</Base>
                            </div>

                            <div className="flex w-full flex-row justify-center md:ml-8 md:w-80 md:justify-start">
                              <CopyButton
                                text={depositQuote.toString()}
                                iconClassName={"text-black dark:text-white"}
                                succesMessage={
                                  t("atoms:molecules.coin_layout.header_amount") +
                                  " " +
                                  t(
                                    "kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item"
                                  )
                                }
                              />
                              {loadingQuote ? (
                                <Loader className="ml-3"></Loader>
                              ) : (
                                <Base className="ml-3 font-semibold">{depositQuote}</Base>
                              )}
                            </div>
                          </div>
                          <div className="mt-1 flex w-full flex-col justify-center md:flex-row">
                            <div className="text-center text-black md:w-40 md:text-right dark:text-white">
                              <Base>
                                {" "}
                                {`${t(
                                  "kypay.modal_deposit_funds.steps.step_2.crypto.label_wallet"
                                )}:`}
                              </Base>
                            </div>
                            <div className="flex w-full flex-row justify-center md:ml-8 md:w-80 md:justify-start">
                              <CopyButton
                                text={walletAddressDeposit ?? ""}
                                iconClassName={"text-black dark:text-white"}
                                succesMessage={
                                  t("kypay.modal_deposit_funds.steps.step_2.crypto.label_wallet") +
                                  " " +
                                  t(
                                    "kypay.modal_deposit_funds.steps.step_2.bank_transfer.copied_item"
                                  )
                                }
                              />
                              <Base className="ml-3 line-clamp-2 font-semibold md:line-clamp-none">
                                {walletAddressDeposit}
                              </Base>
                            </div>
                          </div>

                          <div className="mt-1 flex w-full flex-col justify-center md:flex-row">
                            <div className="text-center text-black md:w-40 md:text-right dark:text-white">
                              <Base>
                                {" "}
                                {t("kypay.modal_deposit_funds.steps.step_2.crypto.label_qr")}
                              </Base>
                            </div>
                            <div className="flex w-full flex-row justify-center md:ml-8 md:w-80 md:justify-start">
                              <QRCodeSVG value={walletAddressDeposit ?? ""} size={100} />
                            </div>
                          </div>

                          <div className="mt-3 text-left font-semibold text-sm md:mt-0 md:p-6 md:text-base">
                            <Base className="md:px-10">
                              {t("kypay.modal_deposit_funds.steps.step_2.crypto.qr_description", {
                                crypto: blockchain
                              })}
                            </Base>
                          </div>
                          <div className={"mt-4 flex"}>
                            <Alert
                              type={AlertType.Info}
                              title={t(
                                "kypay.modal_deposit_funds.steps.step_2.bank_transfer.alert.title"
                              )}
                            >
                              <InfoAlert>
                                {t("kypay.modal_deposit_funds.steps.step_2.crypto.crypto_info")}
                              </InfoAlert>
                            </Alert>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </>
              </div>
            )}

          {sessionId === null &&
            (selectedPaymentTools !== PaymentTools.CRYPTO ||
              (travelWalletRuleUuid && selectedPaymentTools === PaymentTools.CRYPTO)) && (
              <div className="flex flex-row justify-center">
                <Button
                  disabled={
                    !amount ||
                    !amountString ||
                    !acceptCGU ||
                    (selectedPaymentTools === PaymentTools.KYPAY &&
                      (((kypay as number) < amount) as boolean)) ||
                    (selectedPaymentTools === PaymentTools.CRYPTO &&
                      (!quoteData || !walletAddressDeposit || amount < 1.5))
                  }
                  className={"mt-6 w-max"}
                  theme={Themes.primary}
                  loading={operationInProgress || pendingKypayParticipate}
                  onClick={
                    selectedPaymentTools === PaymentTools.KYPAY ||
                    selectedPaymentTools === PaymentTools.BANK_TRANSFER ||
                    selectedPaymentTools === PaymentTools.CRYPTO
                      ? handleNextStepEvent
                      : handleCardPAyment
                  }
                >
                  {selectedPaymentTools === PaymentTools.KYPAY ||
                  selectedPaymentTools === PaymentTools.CARD ||
                  selectedPaymentTools === PaymentTools.CRYPTO
                    ? btnText
                      ? btnText
                      : t("button.fund_bank")
                    : t("button.fund_bank")}
                </Button>
              </div>
            )}

          {errorPaymentCard && sessionId && (
            <div className="flex flex-row justify-center">
              {" "}
              <Button
                className={"mt-6 w-1/2"}
                theme={Themes.primary}
                onClick={() => {
                  props?.onClose?.();

                  setSessionId(null);
                  setReferenceId(null);
                  setErrorPaymentCard(false);
                }}
              >
                {t("pass:olkypass.general.close")}
              </Button>{" "}
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
